import PlusIcon from '@/assets/icons/plus.svg?react';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { ExtendedTarificationFormData } from '@/types/benefits/tarification/TarificationFormData';
import {
  ArrayPath,
  type Control,
  FieldArray,
  Path,
  PathValue,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledButton from '../styled/StyledButton';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import StyledRadioGroup, {
  YesNoStyledRadioGroup,
} from '../styled/StyledRadioGroup';
import TarifCard from './TarifCard';
import { useTranslation } from 'react-i18next';

interface TarifListProps<FieldsType extends ExtendedTarificationFormData> {
  control: Control<FieldsType>;
  benefitType?: BenefitType;
  isViewMode?: boolean;
}

const TarifList = <FieldsType extends ExtendedTarificationFormData>({
  control,
  benefitType = BenefitType.ACTIVITY,
  isViewMode = false,
}: TarifListProps<FieldsType>) => {
  const { t } = useTranslation();
  const { resetField } = useFormContext<FieldsType>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'tarifs' as ArrayPath<FieldsType>, // Path to the tarifs field in the form
  });
  const createDefaultTarif = () =>
    ({
      name: '',
      periods: [
        {
          startDate: undefined,
          endDate: undefined,
          applicationDays: [],
        },
      ],
      pricePartitions: [
        {
          price: undefined,
          from: 1,
          to: 1,
          specialPrice: undefined,
        },
      ],
    } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);

  const tarifType = useWatch({
    control,
    name: 'tarifType' as Path<FieldsType>,
  });
  const customTarif =
    useWatch({
      name: 'customTarif' as Path<FieldsType>,
      control,
    }) === 'true';
  const tarifs = useWatch({
    control,
    name: 'tarifs' as Path<FieldsType>,
  }) as ExtendedTarificationFormData['tarifs'];

  return (
    <StyledFormSection sectionTitle={t('tariffs.addTarif')}>
      {(benefitType === BenefitType.ACTIVITY ||
        benefitType === BenefitType.VOL) && (
        <>
          {benefitType === BenefitType.ACTIVITY && (
            <StyledRadioGroup
              control={control}
              name={'tarifType' as Path<FieldsType>}
              options={[
                {
                  label: t('tariffs.tarifType.perPrestation'),
                  value: 'par-prestation',
                },
                {
                  label: t('tariffs.tarifType.perPerson'),
                  value: 'par-personne',
                },
              ]}
              disabled={isViewMode}
            />
          )}
          {tarifType === 'par-personne' && (
            <>
              <p>{t('tariffs.customTarifQuestion')}</p>
              <YesNoStyledRadioGroup
                control={control}
                name={'customTarif' as Path<FieldsType>}
                disabled={isViewMode}
              />
              {customTarif && (
                <>
                  <p>{t('tariffs.childAgeRange')}</p>
                  <div className="flex gap-2 items-center">
                    <label className="text-gray-700">
                      {t('tariffs.childAgeFrom')}
                    </label>
                    <div className="w-14">
                      <StyledInput
                        control={control}
                        name={'childAgeFrom' as Path<FieldsType>}
                        type="number"
                        min={0}
                        disabled={isViewMode}
                      />
                    </div>
                    {t('tariffs.childAgeTo')}
                    <label className="text-gray-700">
                      {t('tariffs.childAgeFrom')}
                    </label>
                    <div className="w-14">
                      <StyledInput
                        control={control}
                        name={'childAgeTo' as Path<FieldsType>}
                        type="number"
                        min={1}
                        disabled={isViewMode}
                      />
                    </div>
                    {t('tariffs.childAgeIncluded')}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
      {fields.map((item, index) => (
        <TarifCard
          key={item.id}
          control={control}
          index={index}
          duplicateTarif={() => {
            append({
              name:
                tarifs && tarifs[index].name
                  ? `${tarifs[index].name} (copie)`
                  : `tarif ${fields.length + 1}`,
              periods: tarifs[index].periods,
              pricePartitions: tarifs[index].pricePartitions,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0]);
          }}
          deleteTarif={() => {
            if (fields.length > 1) {
              remove(index);
              return;
            }
            const resetTarif = createDefaultTarif();
            replace([resetTarif]);
            resetField('tarifs.0' as Path<FieldsType>, {
              defaultValue: resetTarif as PathValue<
                FieldsType,
                Path<FieldsType>
              >,
            });
          }}
          tarifData={
            tarifs && tarifs.length > index ? tarifs[index] : undefined
          }
          customTarif={customTarif}
          tarifType={tarifType as 'par-prestation' | 'par-personne'}
          isViewMode={isViewMode}
          benefitType={benefitType}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('tariffs.addTarif')}
          onClick={() => append(createDefaultTarif())}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </StyledFormSection>
  );
};

export default TarifList;
